import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dt, title } from "@/lib/format";

const RESOURCES = ["campaign", "agent", "contact", "contact_list", "call", "lead", "callback",
  "do_not_call", "phone_number", "budget", "user", "provider", "settings", "quality_review", "system"];

export default function AuditLog() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [resourceType, setResourceType] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["audit", page, q, resourceType],
    queryFn: () => get("/audit-logs", { page, page_size: 25, q: q || undefined, resource_type: resourceType || undefined }),
  });

  return (
    <>
      <PageHeader testId="audit-page" title="Audit log" subtitle="Every configuration and compliance-relevant change with old and new values" />

      <Section
        testId="audit-section"
        title={`${data?.total ?? 0} entries`}
        actions={
          <div className="flex gap-2">
            <Input data-testid="audit-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search action, user or resource" className="h-9 w-56" />
            <Select value={resourceType || "all"} onValueChange={(v) => { setResourceType(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger data-testid="audit-resource-filter" className="h-9 w-44"><SelectValue placeholder="Resource" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All resources</SelectItem>
                {RESOURCES.map((r) => <SelectItem key={r} value={r}>{title(r)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      >
        <DataTable
          testId="audit-table"
          rowTestId="audit-row"
          loading={isLoading}
          columns={[
            { key: "created_at", label: "Timestamp", render: (r) => dt(r.created_at) },
            { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs font-medium text-slate-800">{r.action}</span> },
            { key: "resource_type", label: "Resource", render: (r) => title(r.resource_type) },
            { key: "resource_name", label: "Target", render: (r) => r.resource_name || r.resource_id || "—" },
            { key: "user_email", label: "User" },
            { key: "user_role", label: "Role", render: (r) => title(r.user_role) },
            { key: "ip_address", label: "IP", render: (r) => <span className="font-mono text-xs">{r.ip_address || "—"}</span> },
            {
              key: "old_value",
              label: "Old value",
              render: (r) => <span className="line-clamp-1 block max-w-[16rem] font-mono text-[11px] text-rose-700">{r.old_value ? JSON.stringify(r.old_value) : "—"}</span>,
            },
            {
              key: "new_value",
              label: "New value",
              render: (r) => <span className="line-clamp-1 block max-w-[16rem] font-mono text-[11px] text-emerald-700">{r.new_value ? JSON.stringify(r.new_value) : "—"}</span>,
            },
          ]}
          rows={data?.items || []}
          empty={{ title: "No audit entries" }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>
    </>
  );
}
